import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BookOpen, Dumbbell, Search, Star, StarOff } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { WordSense } from "@preptrack/contracts";

import { Button, EmptyState, LoadingLine, PageHeader, Tag } from "@/components/primitives";
import { apiFetch } from "@/lib/api";
import { WordSenses } from "@/components/word-senses";
import { WordPronunciation } from "@/components/word-pronunciation";

export type WordCollectionKind = "favorites" | "mistakes";

interface CollectionWord {
  wordId: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  senses: WordSense[];
  markedAt: string;
  cleanReviewDays?: number;
  recallErrors?: number;
  spellingErrors?: number;
  study: {
    learningCount: number;
    masteryStage: "unlearned" | "introduced" | "spelling" | "reinforcing" | "stable";
    masteryLabel: string;
    retrievability: number | null;
    due: boolean;
    dueOn: string | null;
  };
}

const formatter = new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric" });

const COPY = {
  favorites: {
    label: "收藏词汇",
    title: "收藏词汇",
    description: "回看查词时留下的重点词汇，进入词条继续学习或取消收藏。",
    emptyTitle: "还没有收藏词汇",
    emptyDescription: "查词时点击收藏，重点词汇会集中出现在这里。",
  },
  mistakes: {
    label: "错词复习",
    title: "近期错词",
    description: "由记忆反馈自动维护。正确掌握后，词条会从这里移除。",
    emptyTitle: "近期没有错词",
    emptyDescription: "继续按计划学习，记忆反馈会自动更新这份列表。",
  },
} as const;

export function WordCollectionPage({ kind }: { kind: WordCollectionKind }) {
  const queryClient = useQueryClient();
  const [busyWordId, setBusyWordId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["word-collection", kind],
    queryFn: () => apiFetch<{ kind: WordCollectionKind; words: CollectionWord[] }>("/word-collections/" + kind),
  });
  const words = query.data?.words ?? [];
  const copy = COPY[kind];
  const reviewReadyCount = words.reduce((count, word) => count + (
    word.study.due || word.study.masteryStage === "introduced" || word.study.masteryStage === "spelling" ? 1 : 0
  ), 0);
  const stableCount = words.reduce((count, word) => count + (word.study.masteryStage === "stable" ? 1 : 0), 0);

  const removeFavorite = async (wordId: string) => {
    setBusyWordId(wordId);
    try {
      await apiFetch("/words/" + wordId + "/favorite", { method: "POST" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["word-collection", "favorites"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
    } finally {
      setBusyWordId(null);
    }
  };

  if (query.isLoading) return <div className="page"><LoadingLine /></div>;

  return (
    <div className="page collection-page">
      <PageHeader
        title={copy.title}
        description={copy.description}
        action={kind === "mistakes"
          ? <Link className="button button--primary button--md" to="/app/$subject/review" params={{ subject: "english" }} search={{ mode: "review", scope: "mistakes" }}><BookOpen /> 开始错词复习</Link>
          : <div className="word-library-header-actions"><Link className="button button--secondary button--md" to="/app/$subject/search" params={{ subject: "english" }}><Search /> 继续查词</Link><Link className="button button--primary button--md" to="/app/$subject/practice" params={{ subject: "english" }} search={{ scope: "favorites" }}><Dumbbell /> 自由练习</Link></div>}
      />

      <section className="collection-metrics" aria-label={copy.label + "概括"}>
        <div><strong>{words.length}</strong><span>当前词数</span></div>
        <div><strong>{reviewReadyCount}</strong><span>{kind === "mistakes" ? "可立即复习" : "当前可复习"}</span></div>
        <div><strong>{stableCount}</strong><span>达到稳固</span></div>
      </section>

      {query.isError ? <p className="error-strip" role="alert">列表加载失败，请刷新后重试。</p> : null}

      {!query.isError && words.length === 0 ? (
        <EmptyState
          title={copy.emptyTitle}
          description={copy.emptyDescription}
          action={kind === "favorites"
            ? <Link className="button button--primary button--md" to="/app/$subject/search" params={{ subject: "english" }}>前往查词 <ArrowRight /></Link>
            : <Link className="button button--primary button--md" to="/app/$subject/review" params={{ subject: "english" }}>返回背诵计划 <ArrowRight /></Link>}
        />
      ) : null}

      {words.length > 0 ? (
        <section className="collection-list" aria-label={copy.label}>
          <div className="collection-list__head">
            <span>单词</span>
            <span>释义</span>
            <span>学习状态</span>
            <span>操作</span>
          </div>
          {words.map((word) => (
            <article className="collection-row" key={word.wordId}>
              <div className="collection-row__word">
                <Link to="/app/$subject/words/$word" params={{ subject: "english", word: word.wordId }}>{word.headword}</Link>
                <WordPronunciation wordId={word.wordId} headword={word.headword} phoneticUk={word.phoneticUk} phoneticUs={word.phoneticUs} compact />
                <span>{kind === "favorites" ? "收藏于" : "最近出错于"} {formatter.format(new Date(word.markedAt))}</span>
              </div>
              <div className="collection-row__meaning">
                <WordSenses senses={word.senses} variant="compact" empty="待补充词义" />
              </div>
              <div className="collection-row__study">
                <div><Tag tone={word.study.masteryStage === "stable" ? "success" : word.study.masteryStage === "unlearned" ? "neutral" : "accent"}>{word.study.masteryLabel}</Tag>{word.study.masteryStage === "introduced" || word.study.masteryStage === "spelling" ? <Tag variant="metadata" tone="accent">待继续</Tag> : word.study.due ? <Tag variant="metadata" tone="accent">今日到期</Tag> : null}</div>
                <span>学习 {word.study.learningCount} 次</span>
                <small>记忆率 {word.study.retrievability === null ? "--" : `${word.study.retrievability}%`}</small>
              </div>
              <div className="collection-row__action">
                {kind === "favorites" ? (
                  <Button variant="ghost" size="sm" onClick={() => void removeFavorite(word.wordId)} disabled={busyWordId === word.wordId}>
                    <StarOff /> {busyWordId === word.wordId ? "处理中" : "取消收藏"}
                  </Button>
                ) : (
                  <Link to="/app/$subject/words/$word" params={{ subject: "english", word: word.wordId }} aria-label={"查看 " + word.headword}>
                    <ArrowRight />
                  </Link>
                )}
              </div>
            </article>
          ))}
        </section>
      ) : null}

      {kind === "mistakes" && words.length > 0 ? (
        <footer className="collection-note"><Star /><span>错误发生后，在两个不同日期各完成一次无错练习，词条才会离开近期错词。</span></footer>
      ) : null}
    </div>
  );
}
